import { Button, Textarea } from '@patchbay/ui'
import { AlertTriangle } from 'lucide-react'
import * as React from 'react'
import { requestReturn } from '../board/api'
import type { RileyCard } from '../riley/contracts'

/** The kind this module renders (api/app/agent/cards/registry.py). Internal audience; the customer is never told. */
export const REPORT_KIND = 'contractor_report_received'

interface Discrepancy {
  code: string
  detail: string
  packet_value?: string | null
  report_value?: string | null
}

export interface ReportPayload {
  workOrderId: string
  woNumber: string
  source: string
  notes: string | null
  minutes: number | null
  filename: string | null
  discrepancies: Discrepancy[]
}

const str = (value: unknown): string | null => (typeof value === 'string' && value.trim() ? value : null)

export function reportPayload(payload: Record<string, unknown>): ReportPayload {
  const minutes = Number(payload.minutes)
  return {
    workOrderId: str(payload.work_order_id) ?? '',
    woNumber: str(payload.wo_number) ?? 'this work order',
    source: str(payload.source) ?? 'portal',
    notes: str(payload.notes),
    minutes: Number.isFinite(minutes) && minutes > 0 ? minutes : null,
    filename: str(payload.filename),
    discrepancies: Array.isArray(payload.discrepancies)
      ? payload.discrepancies.filter((d): d is Discrepancy => Boolean(d) && typeof d === 'object')
      : [],
  }
}

export function sourceLabel(source: string): string {
  if (source === 'portal') return 'Submitted by the contractor on their work order page'
  if (source === 'desk_email') return 'Arrived by email on the ticket'
  if (source === 'staff_recorded') return 'Recorded by staff from the contractor’s report'
  if (source === 'staff') return 'Submitted by staff on the contractor’s behalf'
  return 'Forwarded to us'
}

export function timeOnSite(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`
}

/** The decision card body. Notes render as text (React escapes them); nothing here reaches the customer. */
export function ReportCardBody({ payload }: { payload: Record<string, unknown> }) {
  const p = reportPayload(payload)
  return (
    <div className="mt-2 space-y-2 text-sm" data-slot="report-card">
      <p className="font-medium">Contractor report received for {p.woNumber}</p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
        <dt className="text-muted-foreground">From</dt>
        <dd data-slot="report-source">{sourceLabel(p.source)}</dd>
        {p.minutes ? (
          <>
            <dt className="text-muted-foreground">Time on site</dt>
            <dd>{timeOnSite(p.minutes)}</dd>
          </>
        ) : null}
        {p.filename ? (
          <>
            <dt className="text-muted-foreground">Report file</dt>
            <dd className="min-w-0 [overflow-wrap:anywhere]">{p.filename}</dd>
          </>
        ) : null}
      </dl>
      {p.notes ? (
        <blockquote className="whitespace-pre-wrap rounded-md bg-muted/60 px-3 py-2 [overflow-wrap:anywhere]" aria-label="Contractor notes">
          {p.notes}
        </blockquote>
      ) : (
        <p className="text-muted-foreground">No written notes with this report.</p>
      )}
      {p.discrepancies.length ? (
        <ul className="space-y-1" aria-label="Differences to check">
          {p.discrepancies.map((d) => (
            <li
              key={`${d.code}:${d.detail}`}
              className="flex items-start gap-1.5 rounded-md border border-warning/40 bg-warning-soft px-2.5 py-1.5"
              data-slot="report-warning"
            >
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
              <span className="min-w-0 [overflow-wrap:anywhere]">{d.detail}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * Accept & approve / Accept (don't approve yet) / Return… . "Not a report" is the card's own dismiss control.
 * Return proposes a wo_return card from the reason, then confirms that card under this person's own token.
 */
export function ReportCardActions({
  card,
  busy,
  onAccept,
  onConfirmReturn,
}: {
  card: RileyCard
  busy: boolean
  onAccept: (payload?: Record<string, unknown>) => unknown
  onConfirmReturn: (returned: RileyCard) => Promise<unknown>
}) {
  const [returning, setReturning] = React.useState(false)
  const [reason, setReason] = React.useState('')
  const [working, setWorking] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [sent, setSent] = React.useState(false)
  const woId = reportPayload(card.payload).workOrderId
  const disabled = busy || working
  const canReturn = Boolean(reason.trim()) && Boolean(woId) && !disabled

  const sendBack = async () => {
    if (!canReturn) return
    setWorking(true)
    setError(null)
    try {
      const returned = (await requestReturn(woId, reason.trim())) as unknown as RileyCard
      await onConfirmReturn(returned)
      setSent(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The work order could not be returned.')
    } finally {
      setWorking(false)
    }
  }

  if (sent) return <p className="text-sm text-muted-foreground">Returned. The work order is back with the contractor.</p>

  return (
    <>
      <Button variant="primary" disabled={disabled} onClick={() => onAccept()} title="Accept (a)">
        Accept & approve
      </Button>
      <Button disabled={disabled} onClick={() => onAccept({ ...card.payload, approve: false })}>
        Accept (don't approve yet)
      </Button>
      <Button disabled={disabled} aria-expanded={returning} onClick={() => setReturning((open) => !open)}>
        Return…
      </Button>
      {returning ? (
        <div className="basis-full space-y-2" data-slot="report-return">
          <label className="block text-xs font-medium" htmlFor={`return-reason-${card.id}`}>
            Reason for returning this report (required)
          </label>
          <Textarea id={`return-reason-${card.id}`} value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}
          <Button variant="primary" size="sm" disabled={!canReturn} onClick={() => void sendBack()}>
            Return to contractor
          </Button>
        </div>
      ) : null}
    </>
  )
}
